import { Link } from 'react-router-dom';
import { useSEO } from '@/hooks/useSEO';
import { categories } from '@/mocks/categories';

const values = [
  { icon: 'ri-compass-3-line', title: '聚合推荐', desc: '汇聚多题材韩漫资源，通过专题与榜单，帮你高效发现值得一追的作品。' },
  { icon: 'ri-filter-3-line', title: '精准筛选', desc: '多层级题材分类与排序筛选，配合站内搜索，想看的题材一键直达。' },
  { icon: 'ri-heart-3-line', title: '个性收藏', desc: '支持本地收藏感兴趣的漫画，无需注册登录，刷新后依然保留。' },
  { icon: 'ri-shield-check-line', title: '安心阅读', desc: '清晰的信息层级与友好的阅读体验，让每一次浏览都轻松愉快。' },
];

const notices = [
  '本站为韩漫漫画资源的聚合与推荐演示平台，站内作品信息均为演示数据。',
  '本站不存储、不提供任何漫画资源下载，所有漫画版权均归原作者及合法发行方所有。',
  '站内收藏功能基于浏览器本地存储实现，数据仅保存在当前设备，清除浏览器数据后可能丢失。',
  '章节阅读页仍在开发中，点击章节将提示为演示范围，敬请期待功能完善。',
];

export default function AboutPage() {
  useSEO({
    title: '关于我们 - 韩漫漫画聚合推荐平台',
    description: '了解韩漫平台的定位与使命、内容范围、服务宗旨、联系方式以及使用须知与版权声明。',
    keywords: '韩漫,关于我们,韩漫平台,漫画推荐,版权声明',
    path: '/about',
  });

  return (
    <div className="w-full">
      <section className="w-full bg-accent-950 text-background-50">
        <div className="mx-auto w-full max-w-[1280px] px-4 md:px-8 py-14 md:py-20">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-background-50/10 text-xs font-label backdrop-blur">
            <i className="ri-book-open-line text-accent-300"></i>
            ABOUT HANMAN
          </span>
          <h1 className="mt-4 font-heading font-extrabold text-3xl md:text-5xl">关于「韩漫」</h1>
          <p className="mt-4 max-w-3xl text-sm md:text-base leading-relaxed text-background-200/90">
            韩漫是一个专注于韩国漫画资源聚合与推荐的阅读平台，致力于为喜爱韩漫的中文读者提供便捷、愉悦的发现与浏览体验，
            让好作品不再被埋没。
          </p>
        </div>
      </section>

      <div className="mx-auto w-full max-w-[1280px] px-4 md:px-8 py-12 md:py-16 flex flex-col gap-12 md:gap-16">
        <section>
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">平台使命</h2>
          <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-700 max-w-4xl">
            我们相信，好的漫画值得被更多人看见。韩漫平台以「聚合 + 推荐」为核心，
            通过清晰的题材体系、实时的热门更新与用心的专题策划，帮助读者用更少的时间找到更对味的作品。
            我们不追求大而全，而是希望做一个懂读者口味、值得信赖的韩漫发现入口。
          </p>
        </section>

        <section>
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">内容范围</h2>
          <p className="mt-3 text-sm md:text-base text-foreground-600">当前平台已覆盖以下八大题材，并持续扩充中。</p>
          <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-4">
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.id}`}
                className="flex items-center gap-3 p-4 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors cursor-pointer"
              >
                <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                  <i className={`${cat.icon} text-xl`}></i>
                </span>
                <div className="min-w-0">
                  <p className="font-heading font-bold text-foreground-950">{cat.name}</p>
                  <p className="text-xs text-foreground-500 truncate">点击浏览</p>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section>
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">服务宗旨</h2>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {values.map((v) => (
              <div key={v.title} className="p-5 rounded-lg bg-background-100 border border-background-200">
                <span className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-100 text-accent-700">
                  <i className={`${v.icon} text-2xl`}></i>
                </span>
                <h3 className="mt-4 font-heading font-bold text-lg text-foreground-950">{v.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{v.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="rounded-lg bg-primary-50 border border-primary-100 p-6 md:p-8">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">联系方式与反馈</h2>
          <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-700 max-w-3xl">
            如果你对平台有任何建议、内容纠错或合作意向，欢迎通过以下渠道联系我们。我们会在收到后尽快回复。
          </p>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="flex items-center gap-3 p-4 rounded-lg bg-background-50 border border-primary-100">
              <i className="ri-mail-line text-2xl text-primary-500"></i>
              <div>
                <p className="text-xs text-foreground-500">邮箱</p>
                <p className="text-sm font-medium text-foreground-900">contact@hanman.example.com</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 rounded-lg bg-background-50 border border-primary-100">
              <i className="ri-wechat-line text-2xl text-accent-600"></i>
              <div>
                <p className="text-xs text-foreground-500">微信</p>
                <p className="text-sm font-medium text-foreground-900">hanman_official</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-4 rounded-lg bg-background-50 border border-primary-100">
              <i className="ri-time-line text-2xl text-secondary-500"></i>
              <div>
                <p className="text-xs text-foreground-500">响应时间</p>
                <p className="text-sm font-medium text-foreground-900">工作日 1-2 天内</p>
              </div>
            </div>
          </div>
          <p className="mt-4 text-xs text-foreground-500">以上联系方式为演示信息，暂未开通实际提交表单。</p>
        </section>

        <section>
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">使用须知与版权声明</h2>
          <ul className="mt-5 flex flex-col gap-3">
            {notices.map((item) => (
              <li key={item} className="flex items-start gap-3 text-sm md:text-base text-foreground-700">
                <i className="ri-checkbox-circle-line text-accent-600 text-xl shrink-0 mt-0.5"></i>
                <span className="leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8 text-center">
          <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-950">准备好开始探索了吗？</h2>
          <p className="mt-2 text-sm text-foreground-600">回到首页，发现更多热门与新鲜的韩漫作品。</p>
          <div className="mt-5 flex flex-wrap justify-center gap-3">
            <Link
              to="/"
              className="inline-flex items-center gap-1.5 px-6 h-11 rounded-md bg-primary-500 text-background-50 text-sm font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-home-line"></i>
              返回首页
            </Link>
            <Link
              to="/category/romance"
              className="inline-flex items-center gap-1.5 px-6 h-11 rounded-md border border-background-300 text-foreground-700 text-sm font-label font-medium hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-compass-3-line"></i>
              浏览热门题材
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}